// src/pages/buyer/GiftCardsPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Gift } from 'lucide-react'
import { giftCardsApi } from '../../api/services'
import { Card, CardHeader, CardBody, Button, PageSpinner, StatusBadge } from '../../components/common/ui'
import toast from 'react-hot-toast'

const AMOUNTS = [500, 1000, 2000, 5000, 10000]

export default function GiftCardsPage() {
  const qc = useQueryClient()
  const [form, setForm] = useState({ value: 1000, recipient_email: '', recipient_name: '', message: '' })
  const [showPurchase, setShowPurchase] = useState(false)

  const { data, isLoading } = useQuery({ queryKey: ['gift-cards'], queryFn: () => giftCardsApi.myCards() })
  const purchase = useMutation({
    mutationFn: () => giftCardsApi.purchase(form),
    onSuccess: () => { toast.success('Gift card sent! 🎁'); qc.invalidateQueries(['gift-cards']); setShowPurchase(false) },
    onError: e => toast.error(e.response?.data?.message || 'Purchase failed'),
  })

  if (isLoading) return <PageSpinner />
  const cards = data?.data?.data || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold flex items-center gap-2"><Gift size={20} className="text-pink" /> Gift Cards</h1>
        <Button size="sm" onClick={() => setShowPurchase(!showPurchase)}>Send a Gift Card</Button>
      </div>

      {showPurchase && (
        <Card className="mb-6">
          <CardHeader><span className="font-bold text-sm">Purchase Gift Card</span></CardHeader>
          <CardBody className="space-y-4">
            <div>
              <p className="text-xs font-bold text-muted uppercase tracking-wider mb-2">Amount</p>
              <div className="flex gap-2 flex-wrap">
                {AMOUNTS.map(a => (
                  <button key={a} onClick={() => setForm(f => ({ ...f, value: a }))}
                    className={`px-4 py-2 rounded-full text-sm border transition-all ${form.value === a ? 'border-pink bg-pink-light text-pink font-bold' : 'border-border hover:border-pink'}`}>
                    ₹{a.toLocaleString('en-IN')}
                  </button>
                ))}
              </div>
            </div>
            <input className="input w-full" placeholder="Recipient email *" value={form.recipient_email} onChange={e => setForm(f => ({ ...f, recipient_email: e.target.value }))} />
            <input className="input w-full" placeholder="Recipient name *" value={form.recipient_name} onChange={e => setForm(f => ({ ...f, recipient_name: e.target.value }))} />
            <textarea className="input w-full h-20" placeholder="Personal message (optional)" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
            <Button loading={purchase.isPending} onClick={() => purchase.mutate()} disabled={!form.recipient_email || !form.recipient_name}>
              Send Gift Card — ₹{Number(form.value).toLocaleString('en-IN')}
            </Button>
          </CardBody>
        </Card>
      )}

      {cards.length === 0 && !showPurchase && (
        <div className="text-center py-12 text-muted">
          <p className="text-4xl mb-3">🎁</p>
          <p className="font-bold text-gray-900">No gift cards yet</p>
          <p className="text-sm">Send one to someone special!</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {cards.map(card => (
          <Card key={card.id}>
            <CardBody>
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm font-bold text-gray-900">{card.code}</span>
                <StatusBadge status={card.status} />
              </div>
              <p className="text-2xl font-display font-bold text-pink">₹{Number(card.balance ?? card.initial_value).toLocaleString('en-IN')}</p>
              {card.recipient_email && <p className="text-xs text-muted mt-1">For: {card.recipient_email}</p>}
              {card.expires_at && <p className="text-xs text-muted">Expires: {new Date(card.expires_at).toLocaleDateString('en-IN')}</p>}
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  )
}
